import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Badge,
  Alert,
  Snackbar
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme} from '@mui/material/styles';
import {
  Calendar,
  Clock,
  Users,
  CalendarPlus,
  X,
  AlertCircle,
  CalendarCheck,
  RefreshCw,
  Copy
} from 'lucide-react';

import consultService from '../services/AGTUps-product-services/consultService';

/** 
 * Paleta de colores / theme 
 * similar a los otros shortcuts
 */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      }
    }
  });

// Función para calcular el tiempo restante
const getTimeRemaining = (scheduledTime) => {
  const now = new Date();
  const scheduled = new Date(scheduledTime);
  const diff = scheduled - now;
  
  if (diff <= 0) return { text: 'En curso', isStarted: true, percentage: 100 };
  
  const totalMinutes = 24 * 60;
  const remainingMinutes = Math.floor(diff / (1000 * 60));
  const percentage = Math.max(0, Math.min(100, 100 - (remainingMinutes / totalMinutes * 100)));
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  
  return { 
    text: `${hours}h ${minutes}m restantes`,
    isStarted: false,
    percentage
  };
};

// Expertos disponibles para seleccionar
const AVAILABLE_EXPERTS = [
  { id: 1, name: 'Estratega', role: 'Business Strategy', avatar: 'ES' },
  { id: 2, name: 'Analista', role: 'Market Analysis', avatar: 'AN' },
  { id: 3, name: 'Asesor', role: 'Financial Advisory', avatar: 'AS' },
  { id: 4, name: 'Innovador', role: 'Innovation Expert', avatar: 'IN' },
  { id: 5, name: 'Consultor', role: 'Business Consultant', avatar: 'CO' },
  { id: 6, name: 'Mentor', role: 'Growth Mentor', avatar: 'ME' }
];

// ======== Componente Shortcut: ScheduledSessionsShortcut ========
function ScheduledSessionsShortcut({ open, onClose }) {
  // Tomamos el modo del theme "padre"
  const parentTheme = useTheme();
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);
  const isDark = parentTheme.palette.mode === 'dark';

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);
  
  // Estados para el diálogo de nueva sesión
  const [sessionTitle, setSessionTitle] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [sessionTime, setSessionTime] = useState('');
  const [selectedExperts, setSelectedExperts] = useState([]);
  const [sessionNotes, setSessionNotes] = useState('');
  
  // Estado para notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Cargar sesiones cuando se abre el shortcut
  useEffect(() => {
    if (open) {
      fetchSessions();
    }
  }, [open]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const res = await consultService.getUpcomingSessions();
      if (res.success && res.data) {
        // Ordenar por scheduledTime ascendente
        const sorted = res.data.sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime));
        setSessions(sorted);
      }
    } catch (error) {
      console.error('Error fetching upcoming sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Mañana';
    } else {
      return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }
  };

  // Mostrar snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Validar formulario
  const validateForm = () => {
    if (!sessionTitle.trim()) {
      showSnackbar('Por favor ingresa un título para la sesión', 'error');
      return false;
    }
    
    if (!sessionDate) {
      showSnackbar('Por favor selecciona una fecha', 'error');
      return false;
    }
    
    if (!sessionTime) {
      showSnackbar('Por favor selecciona una hora', 'error');
      return false;
    }
    
    if (selectedExperts.length === 0) {
      showSnackbar('Selecciona al menos un experto para la sesión', 'error');
      return false;
    }
    
    // Validar que la fecha sea futura
    const selectedDateTime = new Date(`${sessionDate}T${sessionTime}`);
    if (selectedDateTime < new Date()) {
      showSnackbar('La fecha de la sesión debe ser en el futuro', 'error');
      return false;
    }
    
    return true;
  };

  // Crear nueva sesión
  const handleCreateSession = async () => {
    if (!validateForm()) return;
    
    try {
      const selectedDateTime = new Date(`${sessionDate}T${sessionTime}`);
      
      // Simular ID para esta demo
      const consultationId = `session-${Date.now()}`;
      
      const sessionData = {
        consultationId,
        title: sessionTitle,
        expertPanel: selectedExperts,
        scheduledTime: selectedDateTime.toISOString(),
        notes: sessionNotes
      };
      
      // Llamar al servicio para agendar
      const res = await consultService.scheduleMeeting(consultationId, selectedDateTime.toISOString());
      
      if (res.success) {
        // Agregar la nueva sesión a la lista
        setSessions(prev => [...prev, { 
          ...sessionData, 
          status: 'programado',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }].sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime)));
        
        showSnackbar('Sesión programada exitosamente');
        handleCloseNewSessionDialog();
      }
    } catch (error) {
      console.error('Error scheduling session:', error);
      showSnackbar('Error al programar la sesión', 'error');
    }
  };

  // Cancelar una sesión programada
  const handleCancelSession = async (sessionId) => {
    try {
      // En un entorno real, llamaríamos a un servicio para cancelar
      // await consultService.cancelSession(sessionId);
      
      // Eliminar la sesión de la lista
      setSessions(prev => prev.filter(session => session.consultationId !== sessionId));
      showSnackbar('Sesión cancelada correctamente');
    } catch (error) {
      console.error('Error cancelling session:', error);
      showSnackbar('Error al cancelar la sesión', 'error');
    }
  };

  // Abrir diálogo de nueva sesión
  const handleOpenNewSessionDialog = () => {
    // Establecer fecha para mañana a las 12 PM por defecto
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    
    // Formatear fecha para el input type="date"
    const formattedDate = tomorrow.toISOString().split('T')[0];
    
    // Formatear hora para el input type="time"
    const formattedTime = `${tomorrow.getHours().toString().padStart(2, '0')}:${tomorrow.getMinutes().toString().padStart(2, '0')}`;
    
    setSessionDate(formattedDate);
    setSessionTime(formattedTime);
    setSessionTitle('');
    setSelectedExperts([]);
    setSessionNotes('');
    setShowNewSessionDialog(true);
  };

  // Cerrar diálogo de nueva sesión
  const handleCloseNewSessionDialog = () => {
    setShowNewSessionDialog(false);
  };

  // Renderizar el contenido principal del shortcut
  const renderMainContent = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (sessions.length === 0) {
      return (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Calendar size={48} opacity={0.5} />
          <Typography variant="body1" color="text.secondary">
            No hay sesiones programadas.
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<CalendarPlus />}
            onClick={handleOpenNewSessionDialog}
          >
            Programar nueva sesión
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Próximas sesiones
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<CalendarPlus />}
            onClick={handleOpenNewSessionDialog}
            size="small"
          >
            Nueva sesión
          </Button>
        </Box>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Recuerda que dispones de una consulta diaria con duración máxima de 1 hora. Programa tus sesiones estratégicamente.
          </Typography>
        </Alert>
        
        <Grid container spacing={2}>
          {sessions.map((session, index) => {
            const timeInfo = getTimeRemaining(session.scheduledTime);
            const sessionDate = new Date(session.scheduledTime);
            
            return (
              <Grid item xs={12} md={6} key={index}>
                <Card 
                  sx={{ 
                    borderRadius: 2,
                    borderLeft: `4px solid ${timeInfo.isStarted ? '#FF9500' : COLORS.primary}`,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {session.title || 'Sesión programada'}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" mt={0.5}>
                          <Clock size={14} style={{ marginRight: 6, color: timeInfo.isStarted ? '#FF9500' : COLORS.primary }} />
                          <Typography variant="caption" color="text.secondary">
                            {sessionDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                          <Box 
                            component="span" 
                            sx={{ 
                              width: 3, 
                              height: 3, 
                              borderRadius: '50%', 
                              bgcolor: 'text.disabled',
                              mx: 0.8
                            }} 
                          />
                          <Badge 
                            badgeContent={formatDate(session.scheduledTime) === 'Hoy' ? '!' : null}
                            color="primary"
                          >
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(session.scheduledTime)}
                            </Typography>
                          </Badge>
                        </Box>
                      </Box>
                      
                      <Box display="flex" alignItems="center">
                        <Chip
                          label={timeInfo.text}
                          color={timeInfo.isStarted ? 'warning' : 'primary'}
                          size="small"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        <Tooltip title="Cancelar sesión">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelSession(session.consultationId);
                            }}
                            sx={{ ml: 1 }}
                          >
                            <X size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mt: 1, mb: 2 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={timeInfo.percentage}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: timeInfo.isStarted ? '#FF9500' : COLORS.primary,
                          }
                        }}
                      />
                    </Box>
                    
                    {session.notes && (
                      <Box 
                        sx={{ 
                          p: 1.5, 
                          borderRadius: 1, 
                          bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                          mb: 1.5
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {session.notes}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" gap={1}>
                        <Chip
                          size="small"
                          icon={<Users size={12} />}
                          label={`${session.expertPanel?.length || 0} expertos`}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        
                        <Chip
                          size="small"
                          icon={<Clock size={12} />}
                          label="60 minutos"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </Box>
                      
                      <Tooltip title="Copiar enlace de sesión">
                        <IconButton size="small">
                          <Copy size={16} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="scheduled-sessions-dialog"
      >
        {/* Title + Actions */}
        <DialogTitle
          id="scheduled-sessions-dialog"
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Sesiones Programadas
          </Typography>
          <Box>
            <Tooltip title="Actualizar">
              <IconButton onClick={fetchSessions}>
                <RefreshCw size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Nueva sesión">
              <IconButton onClick={handleOpenNewSessionDialog}>
                <CalendarPlus size={20} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        {/* Contenido Principal */}
        <DialogContent dividers sx={{ minHeight: '400px' }}>
          {renderMainContent()}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: parentTheme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
              borderColor: parentTheme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : undefined
            }}
          >
            Cerrar
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<Calendar />}
            onClick={onClose}
          >
            Ver en Calendario
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para programar nueva sesión */}
      <Dialog 
        open={showNewSessionDialog} 
        onClose={handleCloseNewSessionDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center">
            <CalendarPlus size={20} style={{ marginRight: 8 }} />
            Programar nueva sesión
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Box sx={{ my: 1 }}>
            <TextField
              label="Título de la sesión"
              fullWidth
              value={sessionTitle}
              onChange={(e) => setSessionTitle(e.target.value)}
              margin="normal"
              placeholder="Ej: Consulta estratégica, Análisis financiero, etc."
            />
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fecha"
                  type="date"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                  inputProps={{ min: new Date().toISOString().split('T')[0] }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Hora"
                  type="time"
                  value={sessionTime}
                  onChange={(e) => setSessionTime(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                  inputProps={{ step: 900 }}
                />
              </Grid>
            </Grid>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="experts-select-label">Expertos</InputLabel>
              <Select
                labelId="experts-select-label"
                multiple
                value={selectedExperts}
                onChange={(e) => setSelectedExperts(e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((expert) => (
                      <Chip 
                        key={expert.id} 
                        label={expert.name} 
                        size="small" 
                      />
                    ))}
                  </Box>
                )}
              >
                {AVAILABLE_EXPERTS.map((expert) => (
                  <MenuItem key={expert.id} value={expert}>
                    {expert.name} - {expert.role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Notas para la sesión"
              multiline
              rows={3}
              fullWidth
              value={sessionNotes}
              onChange={(e) => setSessionNotes(e.target.value)}
              margin="normal"
              placeholder="Describe brevemente el objetivo de esta sesión..."
            />
            
            <Box sx={{ mt: 2, p: 2, bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <AlertCircle size={20} style={{ color: isDark ? 'rgba(128, 128, 128, 0.7)' : 'rgba(128, 128, 128, 0.7)', marginTop: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Recuerda que dispones de una consulta diaria con duración máxima de 1 hora. 
                  Esta sesión reservará tu tiempo para que puedas consultar con los expertos seleccionados.
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseNewSessionDialog}
            variant="outlined"
            sx={{
              color: parentTheme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
              borderColor: parentTheme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : undefined
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleCreateSession} 
            variant="contained" 
            color="primary" 
            startIcon={<CalendarCheck size={16} />}
          >
            Programar sesión
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default ScheduledSessionsShortcut;